import React, { useEffect, useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  ErrorHandler,
  FullScreenLoadingModal,
  PageSteps,
} from '@common/components';
import { Form } from 'antd';
import { useMutation, useQuery, useNavigate } from '@common/hooks';
import { LongTermRateStepConfirmRateUpdates } from './_rates-long-term-step-summary';
import { RatesLongTermStepUnits } from './_rates-long-term-step-units';
import { RatesLongTermStepForm } from './_rates-long-term-step-form';
import { RatesLongTermConfirmationModal } from './_rates-long-term-confirmation-modal';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import { RatesLongTermStepUpdates } from './_rates-long-term-step-updates';
import {
  useRateCardCreationAttributes,
  useRateCardDeviations,
  useSelectedOrganizationUnits,
  withRateCardCreationContext,
} from './_rates-long-term-context';
import { RATES_URLS } from '@institutions/services';
import {
  BillingCardProgressQuery,
  CreateBillingCardMutation,
} from '@institutions/graphql';

export const modalStatuses = {
  REVIEW: 'review',
  SUCCESS: 'success',
};

export const RatesLongTermCreationSteps = withRateCardCreationContext(() => {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Rates',
      path: RATES_URLS.BASE,
    },
    {
      name: 'Long Term',
      path: RATES_URLS.LONG_TERM,
    },
    {
      name: 'Create Rate Card',
      path: `${RATES_URLS.LONG_TERM}/create`,
    },
  ]);

  const navigate = useNavigate();
  const [rateCardAttributes, setRateCardAttributes] =
    useRateCardCreationAttributes();
  const [selectedOrganizationUnits] = useSelectedOrganizationUnits();
  const [deviations] = useRateCardDeviations();
  const [billingCardId, setBillingCardId] = useState<string | undefined>();
  const [hasMutationAnyError, setHasMutationAnyError] = useState(false);
  const [isProgressPending, setIsProgressPending] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  const {
    data,
    startPolling,
    stopPolling,
    error: queryError,
  } = useQuery(BillingCardProgressQuery, {
    skip: !billingCardId,
    variables: { id: billingCardId || '' },
  });

  useEffect(() => {
    const isPending = data?.billingCard?.pending ?? true;

    setIsProgressPending(isPending);

    if (!isPending) {
      stopPolling();
      setIsSuccessModalVisible(true);
    }
  }, [data?.billingCard?.pending, stopPolling]);

  const [createBillingCard, { loading: mutationLoading }] = useMutation(
    CreateBillingCardMutation
  );

  const [stepTwoForm] = Form.useForm();

  if (queryError) {
    return <ErrorHandler error={queryError} log="RatesLongTermCreationSteps" />;
  }

  function handleCloseSuccessModal() {
    setIsSuccessModalVisible(false);
    const effectiveDate = toISODateFormat(rateCardAttributes?.effectiveDate);
    navigate(`/works/admin/rates/long-term?effective-date=${effectiveDate}`);
  }

  async function handleConfirm() {
    const { directSourcingRate, localRate, travelerRate } =
      rateCardAttributes || {};

    const { data, errors } = await createBillingCard({
      variables: {
        directSourcingRate: {
          dayBillingRate: directSourcingRate?.dayBillingRate ?? 0,
          eveningBillingRate: directSourcingRate?.eveningBillingRate ?? 0,
          nightBillingRate: directSourcingRate?.nightBillingRate ?? 0,
          variableBillingRate: directSourcingRate?.variableBillingRate ?? 0,
        },
        localRate: {
          dayBillingRate: localRate?.dayBillingRate ?? 0,
          eveningBillingRate: localRate?.eveningBillingRate ?? 0,
          nightBillingRate: localRate?.nightBillingRate ?? 0,
          variableBillingRate: localRate?.variableBillingRate ?? 0,
        },
        travelerRate: {
          dayBillingRate: travelerRate?.dayBillingRate ?? 0,
          eveningBillingRate: travelerRate?.eveningBillingRate ?? 0,
          nightBillingRate: travelerRate?.nightBillingRate ?? 0,
          variableBillingRate: travelerRate?.variableBillingRate ?? 0,
        },
        billingCard: {
          roleId: rateCardAttributes.role?.id ?? '',
          roleLevel: rateCardAttributes.roleLevel ?? '',
          effectiveDate: toISODateFormat(rateCardAttributes.effectiveDate),
        },
        organizationUnits: selectedOrganizationUnits.map(({ id }) => id),
        deviatedEntities: {
          staffingRequests: [
            ...deviations.pendingStaffingRequests,
            ...deviations.openStaffingRequests,
          ],
          offers: deviations.pendingOffers,
          extensionOffers: deviations.extensionOffers,
        },
      },
    });

    if (errors) {
      setHasMutationAnyError(true);
      return;
    }

    const newBillingCardId = data?.createBillingCard?.billingCard?.id;
    setBillingCardId(newBillingCardId);

    if (newBillingCardId) {
      startPolling(3000);
    }
  }

  return (
    <>
      <FullScreenLoadingModal
        open={!!billingCardId && isProgressPending}
        progress={data?.billingCard?.progress ?? 0}
        description="Updates in progress..."
      />
      <RatesLongTermConfirmationModal
        type="success"
        visible={isSuccessModalVisible}
        title={`${data?.billingCard?.totalEntitiesAssociated} existing items have been updated successfully`}
        message="Suppliers have been notified by email where applicable."
        onOk={handleCloseSuccessModal}
      />
      <RatesLongTermConfirmationModal
        type="error"
        visible={hasMutationAnyError}
        loading={mutationLoading}
        title="Update failure! No changes were made."
        message="No rate updates were made and the rate card has not been saved.
          You can try again or back to the previous screen."
        onCancel={() => setHasMutationAnyError(false)}
        onOk={() => {
          handleConfirm();
          setHasMutationAnyError(false);
        }}
      />
      <PageSteps
        confirmLoading={mutationLoading}
        onConfirm={handleConfirm}
        onCancel={() => {
          navigate(RATES_URLS.LONG_TERM);
        }}
        steps={[
          {
            title: 'Units',
            page: <RatesLongTermStepUnits />,
            forwardButtonDisabled: !selectedOrganizationUnits.length,
          },
          {
            title: 'Rates',
            page: (
              <RatesLongTermStepForm
                form={stepTwoForm}
                onFormSubmitted={setRateCardAttributes}
              />
            ),
            validate: async () => {
              try {
                await stepTwoForm.validateFields();
                stepTwoForm.submit();
                await new Promise((r) => setTimeout(r));
                return true;
              } catch {
                return false;
              }
            },
          },
          {
            title: 'Updates',
            page: <RatesLongTermStepUpdates />,
          },
          {
            title: 'Summary',
            confirmButtonText: 'Confirm',
            page: <LongTermRateStepConfirmRateUpdates />,
          },
        ]}
      />
    </>
  );
});
